/**
 * Error message generators
 * Provides user-friendly error messages for validation failures
 */

import { validators } from './validators';

export const errorMessages = {
  email: (value: string) => {
    if (!value.trim()) return 'Email is required';
    if (!validators.email(value)) return 'Please enter a valid email address';
    return null;
  },

  phone: (value: string) => {
    if (!value.trim()) return 'Phone number is required';
    if (!validators.phone(value)) return 'Please enter a 10-digit phone number';
    return null;
  },

  ein: (value: string) => {
    if (!value.trim()) return 'EIN is required';
    if (!validators.ein(value)) return 'Please enter a 9-digit EIN';
    return null;
  },

  zip: (value: string, fieldName = 'ZIP code') => {
    if (!value.trim()) return `${fieldName} is required`;
    if (!validators.zip(value)) return `${fieldName} contains invalid characters`;
    return null;
  },

  name: (value: string, fieldName = 'Name') => {
    if (!value.trim()) return `${fieldName} is required`;
    if (!validators.name(value)) return `${fieldName} contains invalid characters`;
    return null;
  },

  city: (value: string, fieldName = 'City') => {
    if (!value.trim()) return `${fieldName} is required`;
    if (!validators.city(value)) return `${fieldName} contains invalid characters`;
    return null;
  },

  state: (value: string, format?: 'code' | 'name') => {
    if (!value || !value.trim()) return 'State is required';
    const checkFormat = format || 'code';
    if (!validators.state(value, checkFormat)) {
      if (checkFormat === 'code') {
        return 'Please enter a valid 2-letter US state code';
      }
      return 'Please enter a valid US state name';
    }
    return null;
  },

  password: (value: string) => {
    if (!value) return 'Password is required';
    if (!validators.password(value)) return 'Password must be at least 8 characters';
    return null;
  },

  passwordConfirm: (password: string, confirm: string) => {
    if (!confirm) return 'Please confirm your password';
    if (!validators.passwordMatch(password, confirm)) return 'Passwords do not match';
    return null;
  },

  verificationCode: (value: string, fieldName = 'Verification code') => {
    if (!value.trim()) return `${fieldName} is required`;
    if (!validators.verificationCode(value)) return `${fieldName} must be exactly 6 digits`;
    return null;
  },

  dateOfBirth: (value: string, validateAge = true) => {
    if (!value.trim()) return 'Date of birth is required';
    if (validateAge) {
      if (!validators.age18(value)) return 'You must be at least 18 years old';
    }
    return null;
  },

  required: (value: unknown, fieldName: string) => {
    if (!validators.required(value)) return `${fieldName} is required`;
    return null;
  },

  propertyValue: (value: string, required = true) => {
    if (!value || value.trim() === '') {
      return required ? 'Property value is required' : null;
    }
    if (!validators.propertyValue(value)) return 'Property value must be a valid positive number';
    return null;
  },

  propertyLinks: (links: { url: string }[]) => {
    if (!links || links.length === 0) return 'Property link is required';
    for (const link of links) {
      if (!validators.propertyLink(link.url)) return 'Property link must be a valid URL';
    }
    return null;
  },

  propertyLink: (link: string) => {
    if (!link || link.trim() === '') return 'Property link is required';
    if (!validators.propertyLink(link)) return 'Property link must be a valid URL';
    return null;
  },

  poolAmount: (value: string) => {
    if (!value || value.trim() === '') return 'Pool amount is required';
    if (!validators.poolAmount(value)) return 'Please enter a valid amount (between $1 and $10,000,000)';
    return null;
  },

  roiRate: (value: string) => {
    if (!value || value.trim() === '') return 'ROI rate is required';
    if (!validators.roiRate(value)) return 'Please enter a valid percentage (between 0.1% and 100%)';
    return null;
  },

  term: (value: string) => {
    if (!value || value.trim() === '') return 'Term is required';
    if (!validators.term(value)) return 'Please enter a valid term (between 1 and 1200 months)';
    return null;
  },
};
